export type PlayPhase = 'home' | 'dash' | 'explore' | 'return';

const rand = (min: number, max: number) => min + Math.random() * (max - min);

/**
 * Child-like play: every few seconds the drone zips off to one side of the screen,
 * pokes around there, sometimes skips over to another spot, then comes back home.
 * Produces a horizontal target offset (world units) — the caller springs toward it.
 */
export class PlayRoamer {
  phase: PlayPhase = 'home';
  target = 0;
  private until = 4;
  private lastSide = -1;

  /**
   * @param range max horizontal offset that keeps the drone fully on screen
   * @param paused hold position (being hovered or dragged)
   * @param resting go home and stay (asleep)
   */
  update(t: number, range: number, paused: boolean, resting: boolean): { target: number; phase: PlayPhase } {
    if (range <= 0.05 || resting) {
      this.phase = 'home';
      this.target = 0;
      this.until = Math.max(this.until, t + 3);
      return { target: 0, phase: this.phase };
    }
    if (paused) {
      this.until = Math.max(this.until, t + 1.2);
      return { target: this.target, phase: this.phase };
    }

    this.target = Math.max(-range, Math.min(range, this.target));

    if (t > this.until) {
      switch (this.phase) {
        case 'home': {
          // Usually alternate sides, sometimes go the same way again
          const side = Math.random() < 0.7 ? -this.lastSide : this.lastSide;
          this.lastSide = side;
          this.target = side * range * rand(0.55, 1);
          this.phase = 'dash';
          this.until = t + rand(1, 1.4);
          break;
        }
        case 'dash':
          this.phase = 'explore';
          this.until = t + rand(1.4, 2.8);
          break;
        case 'explore':
          if (Math.random() < 0.45) {
            // Skip over to another spot — either a little hop or across the screen
            const across = Math.random() < 0.5;
            const dir = -Math.sign(this.target || 1);
            this.target = across ? dir * range * rand(0.5, 0.95) : this.target + dir * range * rand(0.25, 0.4);
            this.target = Math.max(-range, Math.min(range, this.target));
            this.lastSide = Math.sign(this.target) || this.lastSide;
            this.phase = 'dash';
            this.until = t + rand(0.9, 1.3);
          } else {
            this.target = 0;
            this.phase = 'return';
            this.until = t + 1.3;
          }
          break;
        case 'return':
          this.phase = 'home';
          this.until = t + rand(3.5, 7);
          break;
      }
    }

    return { target: this.target, phase: this.phase };
  }
}
